/**
 * Temporary launch aid for the Unified Teacher Home (#19803): a two-step coachmark that orients
 * teachers who used the old Classes/Assignments navigation. A follow-up cleanup ticket deletes it
 * wholesale — the whole feature is this directory plus the `HomeTourCallout` wrappers and the
 * `useHomeTour` call in `../Sidemenu/Sidemenu.tsx`.
 */

/**
 * Accounts created on or after this instant onboard directly into the new Home, so they never see
 * the tour. Compared against the session's `inserted_at`, which is the creation timestamp (there is
 * no separate `created_at`). An account with no known creation date is treated as ineligible, so a
 * missing date can't leak the announcement to brand-new teachers.
 *
 * UTC, to match how the backend stores the value: mandark's `Framework.Schema` gives every
 * `timestamps()` the `Framework.Types.Db.DateTime` type, whose Ecto type is `:utc_datetime_usec` and
 * whose `autogenerate/1` stamps `Etc/UTC`. This cutoff therefore selects exactly the cohort a
 * backend `where inserted_at < '2026-07-28'` would.
 *
 * Being one absolute instant, it also classifies every teacher identically regardless of their
 * browser's timezone. The trade-off is that it lands mid-evening on July 27 in the US (8pm ET /
 * 5pm PT), so US accounts created after that count as post-launch.
 */
export const HOME_TOUR_ACCOUNT_CUTOFF = '2026-07-28T00:00:00.000Z';

/** Dismissal is per user per browser, so a shared device still announces to the next teacher. */
export const HOME_TOUR_STORAGE_KEY_PREFIX = 'home_tour_dismissed';

export const getHomeTourStorageKey = (userId: string) =>
  `${HOME_TOUR_STORAGE_KEY_PREFIX}:${userId}`;

export interface HomeTourStep {
  id: string;
  title: string;
  body: string;
}

/** Ordered steps; the class selector leads because it replaces the old class grid. */
export const HOME_TOUR_STEPS = [
  {
    id: 'class-selector',
    title: 'Class Selector',
    body: 'View individual classrooms or all classrooms.'
  },
  {
    id: 'create-new',
    title: 'Create New',
    body: 'Create classrooms, assignments, and more.'
  }
] as const satisfies readonly HomeTourStep[];

export type HomeTourStepId = (typeof HOME_TOUR_STEPS)[number]['id'];

export const isHomeTourRoute = (pathname: string) =>
  pathname === '/home' || pathname.startsWith('/home/');

/** An ISO date-time carrying no timezone designator, which `Date.parse` would read as local time. */
const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;

/**
 * The session serializes `inserted_at` as UTC with a trailing `Z`, but Ecto's default
 * `timestamps()` type would serialize it bare — and `Date.parse` reads a bare date-time as the
 * browser's *local* time, which would shift a teacher's cohort by their UTC offset. Pinning the
 * bare form to UTC keeps the cutoff timezone-invariant either way.
 */
const parseAccountCreatedAt = (signUpDate: string) =>
  Date.parse(NAIVE_DATE_TIME.test(signUpDate) ? `${signUpDate}Z` : signUpDate);

/** `null`/unparseable dates fail closed (no tour) — see `HOME_TOUR_ACCOUNT_CUTOFF`. */
export const isPreLaunchAccount = (signUpDate: string | null | undefined) => {
  if (!signUpDate) {
    return false;
  }
  const signedUpAt = parseAccountCreatedAt(signUpDate);
  return !Number.isNaN(signedUpAt) && signedUpAt < Date.parse(HOME_TOUR_ACCOUNT_CUTOFF);
};
